import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { DatatableColumn, DatatableSorting, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent } from '@shared/components/filters';
import { FilterField, RequestPageParams, FilterValues } from '@shared/models';
import { TransactionsActions, transactionFeature } from '@store';
import { identity, isUndefined, pickBy } from 'lodash-es';

@Component({
  selector: 'app-transaction-journal-activity',
  imports: [CommonModule, RktIconComponent, FilterMenuComponent, RktTableComponent],
  templateUrl: './transaction-journal-activity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionJournalActivityComponent {
  store = inject(Store);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);

  columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 140, colType: 'date' },
    { name: 'FA ID', prop: 'financialAccountId', colType: 'short-id', flexSize: 200 },
    { name: 'Description', prop: 'description', flexSize: 300 },
    { name: 'Entry Type', prop: 'entryType', flexSize: 170, colAlign: 'center' },
    { name: 'Entry State', prop: 'entryState', flexSize: 170, colAlign: 'center' },
    { name: 'Amount', prop: 'amount', flexSize: 150, colType: 'amount', colAlign: 'right' },
    { name: 'Pending Withdrawl Balance', prop: 'pendingWithdraw', flexSize: 240, colType: 'amount', colAlign: 'right' },
    { name: 'Pending Deposit Balance', prop: 'pendingDeposit', flexSize: 150, colType: 'amount', colAlign: 'right' },
    { name: 'Currency', prop: 'currency', flexSize: 150, colAlign: 'center' },
  ];

  filters: FilterField[] = [
    {
      name: 'date',
      displayName: 'Date',
      type: 'DATE',
      options: [
        { label: 'Today', value: 'TODAY' },
        { label: 'Yesterday', value: 'YESTERDAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: '90 days', value: 'DEFAULT' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
      defaultValue: 'DEFAULT',
      props: {
        maxMonths: '15',
      },
    },
    {
      name: 'financialAccountId',
      displayName: 'Financial Account ID',
      type: 'INPUT',
    },
    {
      name: 'entryType',
      displayName: 'Amount Type',
      type: 'RADIO',
      options: [
        { label: 'Credits', value: 'CREDIT' },
        { label: 'Debits', value: 'DEBIT' },
        { label: 'OPENING BALANCE', value: 'OPENING_BALANCE' },
      ],
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
    {
      name: 'entryState',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cleared', value: 'CLEARED' },
        { label: 'Declined', value: 'DECLINED' },
        { label: 'Error', value: 'ERROR' },
        { label: 'New', value: 'NEW' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Settled', value: 'SETTLED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  journalActivity$ = this.store.select(transactionFeature.selectJournalActivity);

  totalElements$ = this.store.select(transactionFeature.selectJournalActivityTotalElements);

  isLoading$ = this.store.select(transactionFeature.selectJournalActivityLoading);

  activeFilters$ = this.store.select(transactionFeature.selectJournalActivityActiveFilters);

  activeFiltersCount$ = this.store.select(transactionFeature.selectJournalActivityActiveFiltersCount);

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  getJournalActivity({ page, size, sortParams }: FetchData): void {
    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    this.store.dispatch(
      TransactionsActions.loadJournalActivity({
        params: {
          page: page ?? 0,
          size,
          sortParams: sortParams ?? this.sortParams,
        },
      }),
    );
  }

  applyFilters(filterValues: FilterValues): void {
    this.store.dispatch(TransactionsActions.setJournalActivityActiveFilters({ filters: { ...pickBy(filterValues, identity) } }));
    this.getJournalActivity({ page: 0 });
  }

  onActivityRowClick(financialAccountId?: string): void {
    if (!financialAccountId) return;
    this.router.navigateByUrl(`/app/financial-accounts/${financialAccountId}`);
  }
}
